import React from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Floating bar shown when one or more rows are selected. Pass action buttons
 * as children. Renders nothing when `count` is 0.
 */
export function BulkActionBar({ count, onClear, children }) {
  if (!count) return null;
  return (
    <div
      className="sticky bottom-4 z-30 mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card/95 p-2.5 shadow-lg backdrop-blur-md"
      data-testid="bulk-action-bar"
    >
      <span className="px-2 text-sm font-[600] text-foreground" data-testid="bulk-selected-count">{count} selected</span>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
      <Button size="sm" variant="ghost" onClick={onClear} className="ml-auto" data-testid="bulk-clear">
        <X className="mr-1 h-4 w-4" /> Clear
      </Button>
    </div>
  );
}

/**
 * Runs `fn` for each element (id or item) with Promise.allSettled and surfaces a
 * summary toast. Reuses the existing single-item endpoints, so server-side guards
 * (publish validation, admin delete protection) still apply per item.
 */
export async function runBulk(items, fn, verb = "Done") {
  const results = await Promise.allSettled(items.map((it) => fn(it)));
  const ok = results.filter((r) => r.status === "fulfilled").length;
  const failed = results.length - ok;
  const reason = results.find((r) => r.status === "rejected")?.reason?.response?.data?.detail;
  const opts = reason ? { description: reason } : undefined;
  if (failed === 0) toast.success(`${verb}: ${ok}`);
  else if (ok === 0) toast.error(`Failed for all ${failed}`, opts);
  else toast.error(`${verb}: ${ok} · ${failed} failed`, opts);
  return { ok, failed };
}
